import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '../api/client.js';
import { Layout } from '../components/Layout.js';

/**
 * Canonical Facts admin page. Two surfaces:
 *
 * 1. **Authoritative overrides** the admin curates manually — each fact rides
 *    in every Knowledge Agent prompt so contradicting fragments are ignored.
 * 2. **Stale-term scanner** — type a candidate stale string and see which
 *    documents reference it. Pre-fills a fact statement on click.
 * 3. **Suggested facts** — the LLM staleness audit script writes flagged
 *    candidates into `canonical_fact_suggestions/`. Admin accepts (promotes
 *    to a canonical fact) or dismisses each.
 *
 * Lifted out of `/admin/documents` so it has its own URL + nav entry —
 * the page is high-leverage enough to deserve a dedicated home.
 */

const ERROR_FALLBACK = 'Something went wrong. Please try again.';

function errorMessage(err: unknown, fallback = ERROR_FALLBACK): string {
  if (err instanceof ApiError) return err.message ?? fallback;
  return err instanceof Error ? err.message : fallback;
}

interface CanonicalFact {
  id: string;
  statement: string;
  tags: string[];
  enabled: boolean;
  updatedAt: string;
  updatedBy: string;
}

type StaleCategory = 'url' | 'email' | 'brand' | 'product_name' | 'person' | 'version' | 'other';

interface CanonicalFactSuggestion {
  id: string;
  staleTerm: string;
  category: StaleCategory;
  rationale: string;
  confidence: number;
  sourceChunkId: string;
  sourceFile: string;
  sourceDocumentTitle: string;
  excerpt: string;
  detectedAt: string;
  reviewStatus: 'pending' | 'accepted' | 'dismissed';
}

interface StaleScanDocHit {
  filename: string;
  documentTitle: string;
  hitCount: number;
  excerpt: string | null;
}

interface StaleScanResult {
  term: string;
  totalHits: number;
  totalDocs: number;
  topDocs: StaleScanDocHit[];
  truncated: boolean;
}

const CATEGORY_CHIP_CLASS: Record<StaleCategory, string> = {
  url: 'bg-nebula-info-bg text-nebula-info',
  email: 'bg-nebula-tint text-nebula-secondary',
  brand: 'bg-nebula-warning-bg text-nebula-warning',
  product_name: 'bg-nebula-warning-bg text-nebula-warning',
  person: 'bg-nebula-gray-5 text-nebula-fg-2',
  version: 'bg-nebula-gray-5 text-nebula-fg-2',
  other: 'bg-nebula-gray-5 text-nebula-fg-2',
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeRegexJs(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightExcerpt(excerpt: string, term: string): string {
  const safeExcerpt = escapeHtml(excerpt);
  if (!term) return safeExcerpt;
  const escapedTermForRegex = escapeRegexJs(escapeHtml(term));
  const re = new RegExp(`(${escapedTermForRegex})`, 'gi');
  return safeExcerpt.replace(
    re,
    '<mark class="bg-nebula-warning-bg text-nebula-warning rounded px-0.5">$1</mark>',
  );
}

export function AdminCanonicalFactsPage(): JSX.Element {
  const [facts, setFacts] = useState<CanonicalFact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const [scanTerm, setScanTerm] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanResult, setScanResult] = useState<StaleScanResult | null>(null);
  const draftRef = useRef<HTMLTextAreaElement | null>(null);

  const [suggestions, setSuggestions] = useState<CanonicalFactSuggestion[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  const [acceptDrafts, setAcceptDrafts] = useState<Record<string, string>>({});
  const [activeAcceptId, setActiveAcceptId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const res = await apiGet<{ facts: CanonicalFact[] }>('/api/v1/admin/canonical-facts');
      setFacts(res.facts);
    } catch (err) {
      setError(errorMessage(err, 'Could not load canonical facts.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSuggestions = useCallback(async (): Promise<void> => {
    setSuggestionsLoading(true);
    try {
      const res = await apiGet<{
        suggestions: CanonicalFactSuggestion[];
        pendingCount: number;
      }>('/api/v1/admin/canonical-facts/suggestions?status=pending&limit=50');
      setSuggestions(res.suggestions);
      setPendingCount(res.pendingCount);
    } catch (err) {
      setSuggestionsError(errorMessage(err, 'Could not load suggestions.'));
    } finally {
      setSuggestionsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadSuggestions();
  }, [load, loadSuggestions]);

  const buildAcceptTemplate = (staleTerm: string): string =>
    `References to "${staleTerm}" in older documentation are outdated. ` +
    `The current authoritative version is [FILL IN]. ` +
    `Treat any reference to "${staleTerm}" as superseded.`;

  const openAcceptForm = (s: CanonicalFactSuggestion): void => {
    setActiveAcceptId(s.id);
    setAcceptDrafts((prev) => ({
      ...prev,
      [s.id]: prev[s.id] ?? buildAcceptTemplate(s.staleTerm),
    }));
  };

  const cancelAcceptForm = (id: string): void => {
    setActiveAcceptId((cur) => (cur === id ? null : cur));
  };

  const onAcceptSuggestion = async (s: CanonicalFactSuggestion): Promise<void> => {
    const statement = (acceptDrafts[s.id] ?? '').trim();
    if (statement.length < 8) {
      setSuggestionsError('Fact must be at least 8 characters.');
      return;
    }
    setBusyId(s.id);
    setSuggestionsError(null);
    try {
      await apiPost(`/api/v1/admin/canonical-facts/suggestions/${s.id}/accept`, {
        statement,
      });
      setActiveAcceptId(null);
      setAcceptDrafts((prev) => {
        const next = { ...prev };
        delete next[s.id];
        return next;
      });
      await Promise.all([load(), loadSuggestions()]);
    } catch (err) {
      setSuggestionsError(errorMessage(err, 'Could not accept suggestion.'));
    } finally {
      setBusyId(null);
    }
  };

  const onDismissSuggestion = async (s: CanonicalFactSuggestion): Promise<void> => {
    setBusyId(s.id);
    setSuggestionsError(null);
    try {
      await apiPost(`/api/v1/admin/canonical-facts/suggestions/${s.id}/dismiss`, {});
      await loadSuggestions();
    } catch (err) {
      setSuggestionsError(errorMessage(err, 'Could not dismiss suggestion.'));
    } finally {
      setBusyId(null);
    }
  };

  const onAdd = async (): Promise<void> => {
    const statement = draft.trim();
    if (!statement) return;
    setSaving(true);
    setError(null);
    try {
      await apiPost('/api/v1/admin/canonical-facts', { statement });
      setDraft('');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not add fact.'));
    } finally {
      setSaving(false);
    }
  };

  const onToggle = async (fact: CanonicalFact): Promise<void> => {
    setError(null);
    try {
      await apiPatch(`/api/v1/admin/canonical-facts/${fact.id}`, {
        enabled: !fact.enabled,
      });
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not update fact.'));
    }
  };

  const onDelete = async (fact: CanonicalFact): Promise<void> => {
    if (!window.confirm(`Delete canonical fact?\n\n"${fact.statement.slice(0, 100)}…"`)) {
      return;
    }
    setError(null);
    try {
      await apiDelete(`/api/v1/admin/canonical-facts/${fact.id}`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not delete fact.'));
    }
  };

  const runScan = async (): Promise<void> => {
    const term = scanTerm.trim();
    if (term.length < 3) {
      setScanError('Enter at least 3 characters.');
      return;
    }
    setScanning(true);
    setScanError(null);
    setScanResult(null);
    try {
      const res = await apiPost<{
        totalHits: number;
        totalDocs: number;
        topDocs: StaleScanDocHit[];
        truncated: boolean;
      }>('/api/v1/admin/canonical-facts/scan', { term });
      setScanResult({ ...res, term });
    } catch (err) {
      setScanError(errorMessage(err, 'Scan failed.'));
    } finally {
      setScanning(false);
    }
  };

  const prefillFactFromScan = (): void => {
    if (!scanResult) return;
    const template =
      `References to "${scanResult.term}" in older documentation are outdated. ` +
      `The current authoritative version is [FILL IN]. ` +
      `Treat any reference to "${scanResult.term}" as superseded.`;
    setDraft(template);
    setTimeout(() => {
      const el = draftRef.current;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
    }, 0);
  };

  const enabledCount = facts.filter((f) => f.enabled).length;

  return (
    <Layout>
      <div className="thin-scroll h-full overflow-y-auto">
        <div className="mx-auto max-w-6xl px-6 py-6">
          <header className="mb-6">
            <h1 className="ov-h-page">Canonical Facts</h1>
            <p className="ov-body mt-1 text-nebula-fg-3">
              Authoritative overrides the Knowledge Agent injects into every answer. Use these to
              keep stale corpus content (old URLs, retired product names, deprecated emails) from
              surfacing as if it were current. {enabledCount} active · {facts.length} total.
            </p>
          </header>

          {error && (
            <div className="mb-4 rounded-md border border-nebula-error/20 bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
              {error}
            </div>
          )}

          {/* Scan corpus for stale terms */}
          <section className="mb-6 rounded-md border border-nebula-border-soft bg-nebula-surface p-4">
            <h2 className="ov-h-section mb-1">Scan corpus for stale terms</h2>
            <p className="ov-meta mb-3 text-nebula-fg-3">
              Find pages that mention an outdated term.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={scanTerm}
                onChange={(e) => setScanTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !scanning) void runScan();
                }}
                placeholder="e.g. firm-it.co.za"
                maxLength={200}
                className="ov-body flex-1 rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm focus:border-nebula-secondary focus:outline-none"
              />
              <button
                type="button"
                onClick={() => void runScan()}
                disabled={scanning || scanTerm.trim().length < 3}
                className="rounded-md bg-nebula-secondary px-4 py-2 text-sm font-ui text-white hover:bg-nebula-secondary/90 disabled:opacity-50"
              >
                {scanning ? 'Scanning…' : 'Scan'}
              </button>
            </div>
            {scanError && <p className="ov-meta mt-2 text-nebula-error">{scanError}</p>}
            {scanResult && !scanning && (
              <div className="mt-3">
                {scanResult.totalHits === 0 ? (
                  <p className="ov-meta text-nebula-fg-3">
                    No matches for <code className="font-mono">{scanResult.term}</code> in the
                    corpus.
                  </p>
                ) : (
                  <>
                    <p className="ov-body mb-2 text-nebula-fg-1">
                      <strong>{scanResult.totalHits}</strong> chunk
                      {scanResult.totalHits === 1 ? '' : 's'} across{' '}
                      <strong>{scanResult.totalDocs}</strong> document
                      {scanResult.totalDocs === 1 ? '' : 's'} matched{' '}
                      <code className="font-mono">{scanResult.term}</code>.
                    </p>
                    <ul className="max-h-[280px] divide-y divide-nebula-border-soft overflow-y-auto rounded-md border border-nebula-border-soft bg-nebula-surface">
                      {scanResult.topDocs.map((d) => (
                        <li key={d.filename} className="px-3 py-2">
                          <div className="flex items-start justify-between gap-2">
                            <span
                              className="ov-body min-w-0 flex-1 truncate text-nebula-fg-1"
                              title={d.documentTitle || d.filename}
                            >
                              {d.documentTitle || d.filename}
                            </span>
                            <span className="ov-meta flex-shrink-0 rounded-full bg-nebula-tint px-2 py-0.5 text-nebula-secondary">
                              {d.hitCount} hit{d.hitCount === 1 ? '' : 's'}
                            </span>
                          </div>
                          {d.excerpt && (
                            <p
                              className="ov-meta mt-1 truncate text-nebula-fg-3"
                              title={d.excerpt}
                              dangerouslySetInnerHTML={{
                                __html: highlightExcerpt(d.excerpt, scanResult.term),
                              }}
                            />
                          )}
                        </li>
                      ))}
                    </ul>
                    {scanResult.truncated && (
                      <p className="ov-meta mt-2 text-nebula-warning">
                        Scan cap reached. Refine the term to narrow.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={prefillFactFromScan}
                      className="mt-3 rounded-md border border-nebula-primary px-3 py-1.5 text-sm font-ui text-nebula-primary hover:bg-nebula-tint"
                    >
                      Add canonical fact for this term
                    </button>
                  </>
                )}
              </div>
            )}
          </section>

          {/* Add canonical fact + list */}
          <section className="mb-6 rounded-md border border-nebula-border-soft bg-nebula-surface p-4">
            <h2 className="ov-h-section mb-3">Active facts</h2>
            <div className="mb-4 flex gap-2">
              <textarea
                ref={draftRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="e.g. The current OneView application URL is oneview.nebula.co.za. Older documentation references firm-it.co.za, which is no longer valid."
                rows={2}
                maxLength={1000}
                className="ov-body min-h-[60px] flex-1 resize-y rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm focus:border-nebula-secondary focus:outline-none"
              />
              <button
                type="button"
                onClick={() => void onAdd()}
                disabled={!draft.trim() || saving}
                className="self-start rounded-md bg-nebula-primary px-4 py-2 text-sm font-ui text-white hover:bg-nebula-primary/90 disabled:opacity-50"
              >
                {saving ? 'Adding…' : 'Add fact'}
              </button>
            </div>

            {loading ? (
              <p className="ov-meta">Loading…</p>
            ) : facts.length === 0 ? (
              <p className="ov-meta text-nebula-fg-3">
                No canonical facts yet. Add the first one above.
              </p>
            ) : (
              <ul className="divide-y divide-nebula-border-soft">
                {facts.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-start justify-between gap-3 py-3"
                    data-enabled={f.enabled}
                  >
                    <div className="min-w-0 flex-1">
                      <p
                        className={`ov-body ${
                          f.enabled ? 'text-nebula-fg-1' : 'text-nebula-fg-3 line-through'
                        }`}
                      >
                        {f.statement}
                      </p>
                      <p className="ov-meta mt-1 text-nebula-fg-3">
                        Updated {new Date(f.updatedAt).toLocaleString('en-GB')} by {f.updatedBy}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => void onToggle(f)}
                        className="rounded-md border border-nebula-border px-2.5 py-1 text-xs text-nebula-fg-1 hover:bg-nebula-tint"
                      >
                        {f.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        type="button"
                        onClick={() => void onDelete(f)}
                        className="rounded-md border border-nebula-border bg-nebula-surface px-2 py-1 text-xs text-nebula-error hover:bg-nebula-error-bg"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Suggested facts queue */}
          <section className="rounded-md border border-nebula-border-soft bg-nebula-surface p-4">
            <h2 className="ov-h-section">
              Suggested facts{' '}
              <span className="ov-meta font-normal text-nebula-fg-3">
                ({pendingCount} pending review)
              </span>
            </h2>
            <p className="ov-meta mb-3 text-nebula-fg-3">
              LLM-flagged candidates from the cross-document audit script. Accept (promote to a
              canonical fact) or dismiss.
            </p>
            {suggestionsError && (
              <div className="mb-3 rounded-md border border-nebula-error/20 bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
                {suggestionsError}
              </div>
            )}
            {suggestionsLoading ? (
              <p className="ov-meta">Loading…</p>
            ) : suggestions.length === 0 ? (
              <p className="ov-meta text-nebula-fg-3">
                No pending suggestions. The audit script will flag candidates here when it runs.
              </p>
            ) : (
              <ul className="divide-y divide-nebula-border-soft rounded-md border border-nebula-border-soft bg-nebula-surface">
                {suggestions.map((s) => {
                  const chipClass = CATEGORY_CHIP_CLASS[s.category] ?? CATEGORY_CHIP_CLASS.other;
                  const sourceLabel = s.sourceDocumentTitle || s.sourceFile;
                  const isAccepting = activeAcceptId === s.id;
                  const isBusy = busyId === s.id;
                  return (
                    <li key={s.id} className="p-3">
                      <div className="flex flex-wrap items-start gap-2">
                        <span className={`ov-meta-upper rounded-full px-2 py-0.5 ${chipClass}`}>
                          {s.category.replace('_', ' ')}
                        </span>
                        <span className="ov-body font-mono text-nebula-fg-1">{s.staleTerm}</span>
                        <span className="ov-meta ml-auto text-nebula-fg-3">
                          {Math.round(s.confidence * 100)}% confidence
                        </span>
                      </div>
                      <p className="ov-meta mt-1 text-nebula-fg-2">{s.rationale}</p>
                      {s.excerpt && (
                        <p
                          className="ov-body mt-2 line-clamp-2 text-nebula-fg-2"
                          dangerouslySetInnerHTML={{
                            __html: highlightExcerpt(s.excerpt, s.staleTerm),
                          }}
                        />
                      )}
                      <p className="ov-meta mt-2 truncate text-nebula-fg-3" title={sourceLabel}>
                        Source: {sourceLabel}
                      </p>
                      {!isAccepting ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => openAcceptForm(s)}
                            disabled={isBusy}
                            className="rounded-md bg-nebula-primary px-3 py-1.5 text-sm font-ui text-white hover:bg-nebula-primary/90 disabled:opacity-50"
                          >
                            Accept
                          </button>
                          <button
                            type="button"
                            onClick={() => void onDismissSuggestion(s)}
                            disabled={isBusy}
                            className="rounded-md border border-nebula-border bg-nebula-surface px-3 py-1.5 text-sm text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-50"
                          >
                            {isBusy ? 'Dismissing…' : 'Dismiss'}
                          </button>
                        </div>
                      ) : (
                        <div className="mt-3 space-y-2">
                          <textarea
                            value={acceptDrafts[s.id] ?? ''}
                            onChange={(e) =>
                              setAcceptDrafts((prev) => ({
                                ...prev,
                                [s.id]: e.target.value,
                              }))
                            }
                            rows={3}
                            maxLength={1000}
                            className="ov-body w-full resize-y rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm focus:border-nebula-secondary focus:outline-none"
                          />
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => void onAcceptSuggestion(s)}
                              disabled={isBusy}
                              className="rounded-md bg-nebula-primary px-3 py-1.5 text-sm font-ui text-white hover:bg-nebula-primary/90 disabled:opacity-50"
                            >
                              {isBusy ? 'Adding…' : 'Add fact'}
                            </button>
                            <button
                              type="button"
                              onClick={() => cancelAcceptForm(s.id)}
                              disabled={isBusy}
                              className="rounded-md border border-nebula-border bg-nebula-surface px-3 py-1.5 text-sm text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    </Layout>
  );
}
